import { useEffect } from 'react';
import { useAtomValue } from 'jotai';
import { accessTokenAtom, currentUserAtom } from '../store';
import {
  loadOutbox,
  outboxSentElsewhere,
  resumeOutbox,
  shareOutboxAuth,
} from '../lib/outbox';
import {
  resumeAttachments,
  resumeAttachmentsForKeys,
} from '../lib/attachments';
import { KEYS_ARRIVED_EVENT } from './useE2EE';
import { markUpdateRequired } from '../lib/updateRequired';
import { useWebSocket } from './useWebSocket';

// Keeps the outbox (M23) going: loads this user's unsent messages, and sends
// them when the app opens, the browser comes back online, or the realtime
// socket reconnects (D33, D34). Mounted once, in the app shell.
export function useOutboxSync() {
  const userId = useAtomValue(currentUserAtom)?.id ?? null;
  const accessToken = useAtomValue(accessTokenAtom);
  const { connectionEpoch } = useWebSocket();

  useEffect(() => {
    let cancelled = false;
    void loadOutbox(userId).then(() => {
      if (userId && !cancelled) resumeOutbox();
    });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // The service worker sends with the latest token while Twinden is closed
  // (M29, D68); signing out takes it away.
  useEffect(() => {
    shareOutboxAuth(userId && accessToken ? { userId, accessToken } : null);
  }, [userId, accessToken]);

  // A reconnect is the most reliable "we're back" signal on phones.
  useEffect(() => {
    if (connectionEpoch > 0) {
      resumeOutbox();
      resumeAttachments();
    }
  }, [connectionEpoch]);

  // Attachments that waited for this device's key in an encrypted chat
  // go again once that chat's keys arrive (M28).
  useEffect(() => {
    const onKeys = (event: Event) => {
      const detail = (event as CustomEvent<{ scope: string; scopeId: string }>)
        .detail;
      if (detail.scope === 'chat') resumeAttachmentsForKeys(detail.scopeId);
    };
    window.addEventListener(KEYS_ARRIVED_EVENT, onKeys);
    return () => window.removeEventListener(KEYS_ARRIVED_EVENT, onKeys);
  }, []);

  useEffect(() => {
    const online = () => {
      resumeOutbox();
      resumeAttachments();
    };
    window.addEventListener('online', online);
    return () => window.removeEventListener('online', online);
  }, []);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; clientId?: string } | null;
      if (data?.type === 'outbox-sent' && data.clientId) {
        outboxSentElsewhere(data.clientId);
      }
      // The service worker's send got a 426 (#82): the update banner.
      if (data?.type === 'update-required') markUpdateRequired();
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () =>
      navigator.serviceWorker.removeEventListener('message', onMessage);
  }, []);
}
